import { api } from './api';

export type AccuracyRuntimeSummaryItem = {
  policy_type?: string | null;
  pipeline_name?: string | null;
  runtime_family?: string | null;
  runtime_version?: string | null;
  source_type?: string | null;
  device_id?: string | null;
  device_count?: number | null;
  reid_enabled?: boolean | null;
  model_versions?: Record<string, string> | null;
  incident_count?: number | null;
  camera_count?: number | null;
  first_ts_start?: string | null;
  last_ts_start?: string | null;
  avg_confidence?: number | null;
};

export type AccuracyRuntimeSummaryResponse = {
  ok: boolean;
  org_id: string;
  site_id: string;
  filters: {
    policy_type?: string | null;
    pipeline_name?: string | null;
    runtime_family?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
  };
  items: AccuracyRuntimeSummaryItem[];
};

export type ReviewSampleItem = {
  id: string;
  sample_type?: string | null;
  sampling_method?: string | null;
  status?: string | null;
  policy_type?: string | null;
  source_incident_id?: string | null;
  device_id?: string | null;
  camera_id?: string | null;
  zone_id?: string | null;
  subject_key?: string | null;
  created_at?: string | null;
  latest_label_outcome?: string | null;
  latest_label_source?: string | null;
  latest_reviewer_identity?: string | null;
  latest_label_notes?: string | null;
  latest_label_payload?: {
    evidence_ref?: string | null;
    reviewer_clip_ref?: string | null;
    reviewer_snapshot_ref?: string | null;
    attachment_refs?: string[] | null;
  } | null;
  latest_labeled_at?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  evaluation_key?: Record<string, any> | null;
  runtime_context?: Record<string, any> | null;
  source_payload?: {
    evidence_alignment?: { frame_aligned?: boolean | null } | null;
    frame_aligned?: boolean | null;
    stored_evidence_refs?: {
      evidence_ref?: string | null;
      snapshot_ref?: string | null;
      video_ref?: string | null;
    } | null;
    [key: string]: any;
  } | null;
  alignment?: {
    proof_present?: boolean;
    state?: 'aligned' | 'quarantined_unaligned' | string;
    has_stored_evidence?: boolean;
  } | null;
};

export type ReviewSamplesResponse = {
  ok: boolean;
  org_id: string;
  site_id: string;
  items: ReviewSampleItem[];
};

export type AccuracyGateCheck = {
  key: string;
  label: string;
  status: 'pass' | 'fail' | 'unknown';
  actual?: number | string | boolean | null;
  threshold?: number | string | boolean | null;
  detail?: string | null;
};

export type EvaluationRunItem = {
  id: string;
  policy_type?: string | null;
  pipeline_name?: string | null;
  runtime_family?: string | null;
  device_id?: string | null;
  camera_id?: string | null;
  labeling_method?: string | null;
  sample_count?: number | null;
  labeled_count?: number | null;
  created_at?: string | null;
  metrics_json?: Record<string, any> | null;
};

export type EvaluationRunsResponse = {
  ok: boolean;
  org_id: string;
  site_id: string;
  items: EvaluationRunItem[];
};

export type AccuracyScorecard = {
  latest_run?: EvaluationRunItem | null;
  previous_run?: EvaluationRunItem | null;
  silent_window_run?: EvaluationRunItem | null;
  comparison?: {
    mode?: 'slice_aligned' | 'legacy_latest_two' | string | null;
    slice_fingerprint?: string | null;
  } | null;
  coverage?: {
    sample_count?: number | null;
    labeled_count?: number | null;
    label_coverage?: number | null;
    insufficient_rate?: number | null;
  } | null;
  recall_signal?: {
    silent_window_miss_rate?: number | null;
    estimated_recall_proxy?: number | null;
  } | null;
  gate_contract?: Record<string, any> | null;
  gate_checks?: AccuracyGateCheck[] | null;
  readiness?: {
    status?: string | null;
    summary?: string | null;
    blockers?: string[] | null;
  } | null;
};

export type AccuracyScorecardResponse = {
  ok: boolean;
  org_id: string;
  site_id: string;
  filters: {
    policy_type?: string | null;
    pipeline_name?: string | null;
    runtime_family?: string | null;
    device_id?: string | null;
    camera_id?: string | null;
  };
  scorecard: AccuracyScorecard;
};

export type SeedReviewSamplesResponse = {
  ok: boolean;
  org_id: string;
  site_id: string;
  count: number;
  items: ReviewSampleItem[];
};

export type ReviewLabelResponse = {
  ok: boolean;
  label: {
    id: string;
    review_sample_id: string;
    label_outcome: string;
    label_source?: string | null;
    notes?: string | null;
    label_payload?: Record<string, any> | null;
  };
};

export type EvaluationRunResponse = {
  ok: boolean;
  evaluation_run: EvaluationRunItem;
};

export type PolicyMeasurementReadinessResponse = {
  ok: boolean;
  policy: {
    policy_id: string;
    policy_type?: string | null;
    camera_id?: string | null;
    device_id?: string | null;
    version?: number | null;
  };
  current_measurement?: Record<string, unknown> | null;
  evaluation_run?: EvaluationRunItem | null;
  recent_evaluation_runs?: EvaluationRunItem[];
  binding_stale?: boolean;
  binding_stale_reason?: string | null;
  readiness?: {
    current_status?: 'MET' | 'NOT_MET' | string | null;
    preview_status_if_admitted?: 'MET' | 'NOT_MET' | string | null;
    evaluation_run_id?: string | null;
    met_ready?: boolean;
    checks?: Array<{ key: string; ok: boolean; detail: string }>;
    next_actions?: string[];
    suggested_supplements?: Record<string, unknown>;
    thresholds?: Record<string, number>;
  } | null;
};

export type MeasurementPlanScheduleItem = {
  ordinal: number;
  stratum: string;
  start: string;
  end: string;
};

export type MeasurementPlan = {
  id: string;
  collection_session_id: string;
  policy_id: string;
  policy_version: number;
  plan_version: number;
  status: string;
  sampling_method: string;
  target_clip_count: number;
  clip_duration_seconds: number;
  collection_window_start: string;
  collection_window_end: string;
  created_at?: string | null;
  plan_payload: {
    schema_version: number;
    frozen: boolean;
    schedule_hash: string;
    plan_hash: string;
    contract: {
      schema_version: number;
      plan_version: number;
      population_start: string;
      population_end: string;
      strata: Array<{ name: string; start: string; end: string }>;
      seed: string;
      clip_duration_seconds: number;
      target_clip_count: number;
      quotas: Record<string, number>;
      identity: {
        policy_id: string;
        policy_version: number;
        config_hash: string;
        model_hash: string;
        threshold_hash: string;
        runtime_identity: string | Record<string, unknown>;
        camera_id: string;
        zone_id: string;
      };
    };
    schedule: MeasurementPlanScheduleItem[];
  };
};

export type MeasurementPlanResponse = { ok: boolean; plan: MeasurementPlan };

export type CreateMeasurementPlanInput = {
  policyId: string;
  policyVersion: number;
  configHash: string;
  modelHash: string;
  thresholdHash: string;
  runtimeIdentity: string | Record<string, unknown>;
  cameraId: string;
  zoneId: string;
  populationStart: string;
  populationEnd: string;
  strata: Array<{ name: string; start: string; end: string }>;
  seed: string;
  clipDurationSeconds: number;
  targetClipCount: number;
  quotas: Record<string, number>;
};

export type MeasurementReviewEvent = {
  event_key: string;
  start_ms: number;
  end_ms: number;
  subject_ref?: string | null;
};

export type MeasurementReviewItem = {
  id: string;
  sampling_plan_id: string;
  sample_ordinal: number;
  camera_id: string;
  clip_started_at: string;
  clip_ended_at: string;
  evidence: {
    storage_uri: string;
    content_sha256: string;
    storage_playable: boolean;
    scheduled_window?: MeasurementPlanScheduleItem | null;
  };
  own_submission?: {
    id: string;
    outcome: 'present' | 'absent' | 'insufficient';
    events: MeasurementReviewEvent[];
    submitted_at: string;
  } | null;
  review_complete: boolean;
};

export type MeasurementReviewQueueResponse = { ok: boolean; items: MeasurementReviewItem[] };

export type MeasurementAnnotation = {
  id: string;
  sampled_clip_id: string;
  annotation_outcome: 'present' | 'absent' | 'insufficient';
  submitted_at: string;
  events: MeasurementReviewEvent[];
};

export type MeasurementEvaluationMetrics = {
  tp: number;
  fn: number;
  fp: number;
  tn_empty_clips: number;
  usable_seconds: number;
  populated_strata: number;
  strata: Record<string, number>;
  positive_opportunities: number;
  paired_reviews: number;
  controlled_scenario_clips: number;
  precision: number | null;
  recall: number | null;
  false_alerts_per_usable_hour: number | null;
  inter_annotator_kappa: number | null;
  precision_wilson_95: { lower: number; upper: number } | null;
  recall_wilson_95: { lower: number; upper: number } | null;
};

export type MeasurementEvaluationPayload = {
  status: 'MET' | 'NOT_MET';
  evaluator_version: string;
  threshold_checks: Record<string, boolean>;
  thresholds: Record<string, number>;
  metrics: MeasurementEvaluationMetrics;
  exclusions: Array<{ clip_id: string; reason: string }>;
  sufficiency: Record<string, boolean>;
};

export type MeasurementEvaluationArtifact = {
  id: string;
  sampling_plan_id: string;
  artifact_version: number;
  content_sha256: string;
  created_at?: string | null;
  metrics: MeasurementEvaluationMetrics;
  artifact_payload: MeasurementEvaluationPayload;
};

export type MeasurementEvaluationResponse = {
  ok: boolean;
  artifact: MeasurementEvaluationArtifact;
};

export type MeasurementFreezeResponse = PolicyMeasurementReadinessResponse & {
  assurance_contract_version: 2;
  freeze: Record<string, unknown>;
  measurement_freeze: Record<string, unknown>;
};

export async function getAccuracyRuntimeSummary(params?: {
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
  limit?: number;
}): Promise<AccuracyRuntimeSummaryResponse> {
  const response = await api.get<AccuracyRuntimeSummaryResponse>('/api/v1/accuracy/runtime-summary', {
    params: {
      policy_type: params?.policyType,
      pipeline_name: params?.pipelineName,
      runtime_family: params?.runtimeFamily,
      device_id: params?.deviceId,
      camera_id: params?.cameraId,
      limit: params?.limit,
    },
  });
  return response.data;
}

export async function getReviewSamples(params?: {
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
  sampleType?: string;
  status?: string;
  limit?: number;
}): Promise<ReviewSamplesResponse> {
  const response = await api.get<ReviewSamplesResponse>('/api/v1/accuracy/review-samples', {
    params: {
      policy_type: params?.policyType,
      pipeline_name: params?.pipelineName,
      runtime_family: params?.runtimeFamily,
      device_id: params?.deviceId,
      camera_id: params?.cameraId,
      sample_type: params?.sampleType,
      status: params?.status,
      limit: params?.limit,
    },
  });
  return response.data;
}

export async function getEvaluationRuns(params?: {
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
  labelingMethod?: string;
  sliceFingerprint?: string;
  limit?: number;
}): Promise<EvaluationRunsResponse> {
  const response = await api.get<EvaluationRunsResponse>('/api/v1/accuracy/evaluation-runs', {
    params: {
      policy_type: params?.policyType,
      pipeline_name: params?.pipelineName,
      runtime_family: params?.runtimeFamily,
      device_id: params?.deviceId,
      camera_id: params?.cameraId,
      labeling_method: params?.labelingMethod,
      slice_fingerprint: params?.sliceFingerprint,
      limit: params?.limit,
    },
  });
  return response.data;
}

export async function getAccuracyScorecard(params?: {
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
}): Promise<AccuracyScorecardResponse> {
  const response = await api.get<AccuracyScorecardResponse>('/api/v1/accuracy/scorecard', {
    params: {
      policy_type: params?.policyType,
      pipeline_name: params?.pipelineName,
      runtime_family: params?.runtimeFamily,
      device_id: params?.deviceId,
      camera_id: params?.cameraId,
    },
  });
  return response.data;
}

export async function getPolicyMeasurementReadiness(
  policyId: string,
  params?: { evaluationRunId?: string; usableSeconds?: number },
): Promise<PolicyMeasurementReadinessResponse> {
  const response = await api.get<PolicyMeasurementReadinessResponse>(
    `/api/v1/accuracy/policies/${encodeURIComponent(String(policyId || '').trim())}/measurement`,
    {
      params: {
        evaluation_run_id: params?.evaluationRunId,
        usable_seconds: params?.usableSeconds,
      },
    },
  );
  return response.data;
}

export async function freezePolicyMeasurement(
  policyId: string,
  evaluationArtifactId: string,
): Promise<MeasurementFreezeResponse> {
  const response = await api.post<MeasurementFreezeResponse>(
    `/api/v1/accuracy/policies/${encodeURIComponent(String(policyId || '').trim())}/measurement/freeze`,
    { evaluation_artifact_id: evaluationArtifactId },
  );
  return response.data;
}

export async function createMeasurementPlan(
  input: CreateMeasurementPlanInput,
): Promise<MeasurementPlanResponse> {
  const response = await api.post<MeasurementPlanResponse>('/api/v1/accuracy/measurement-plans', {
    policy_id: input.policyId,
    policy_version: input.policyVersion,
    config_hash: input.configHash,
    model_hash: input.modelHash,
    threshold_hash: input.thresholdHash,
    runtime_identity: input.runtimeIdentity,
    camera_id: input.cameraId,
    zone_id: input.zoneId,
    population_start: input.populationStart,
    population_end: input.populationEnd,
    strata: input.strata,
    seed: input.seed,
    clip_duration_seconds: input.clipDurationSeconds,
    target_clip_count: input.targetClipCount,
    quotas: input.quotas,
  });
  return response.data;
}

export async function getMeasurementPlan(planId: string): Promise<MeasurementPlanResponse> {
  const response = await api.get<MeasurementPlanResponse>(
    `/api/v1/accuracy/measurement-plans/${encodeURIComponent(planId)}`,
  );
  return response.data;
}

export async function getMeasurementReviewQueue(
  planId: string,
): Promise<MeasurementReviewQueueResponse> {
  const response = await api.get<MeasurementReviewQueueResponse>(
    '/api/v1/accuracy/measurement-review/queue',
    { params: { plan_id: planId, limit: 200 } },
  );
  return response.data;
}

export async function submitMeasurementAnnotation(
  clipId: string,
  input: { outcome: 'present' | 'absent' | 'insufficient'; events: MeasurementReviewEvent[] },
): Promise<{ ok: boolean; annotation: MeasurementAnnotation }> {
  const response = await api.post<{ ok: boolean; annotation: MeasurementAnnotation }>(
    `/api/v1/accuracy/measurement-review/clips/${encodeURIComponent(clipId)}/submit`,
    input,
  );
  return response.data;
}

export async function evaluateMeasurementPlan(
  planId: string,
): Promise<MeasurementEvaluationResponse> {
  const response = await api.post<MeasurementEvaluationResponse>(
    `/api/v1/accuracy/measurement-plans/${encodeURIComponent(planId)}/evaluate`,
    {},
  );
  return response.data;
}

export async function getLatestMeasurementEvaluation(
  planId: string,
): Promise<MeasurementEvaluationResponse> {
  const response = await api.get<MeasurementEvaluationResponse>(
    `/api/v1/accuracy/measurement-plans/${encodeURIComponent(planId)}/evaluation`,
  );
  return response.data;
}

export async function seedReviewSamples(params?: {
  orgId?: string;
  siteId?: string;
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
  limit?: number;
  createdBy?: string;
}): Promise<SeedReviewSamplesResponse> {
  const response = await api.post<SeedReviewSamplesResponse>('/api/v1/accuracy/review-samples/seed', {
    org_id: params?.orgId,
    site_id: params?.siteId,
    policy_type: params?.policyType,
    pipeline_name: params?.pipelineName,
    runtime_family: params?.runtimeFamily,
    device_id: params?.deviceId,
    camera_id: params?.cameraId,
    limit: params?.limit,
    created_by: params?.createdBy,
  });
  return response.data;
}

export async function seedSilentWindowSamples(params?: {
  orgId?: string;
  siteId?: string;
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
  limit?: number;
  windowMinutes?: number;
  lookbackHours?: number;
  strideMinutes?: number;
  createdBy?: string;
}): Promise<SeedReviewSamplesResponse> {
  const response = await api.post<SeedReviewSamplesResponse>('/api/v1/accuracy/review-samples/seed-silent-windows', {
    org_id: params?.orgId,
    site_id: params?.siteId,
    policy_type: params?.policyType,
    pipeline_name: params?.pipelineName,
    runtime_family: params?.runtimeFamily,
    device_id: params?.deviceId,
    camera_id: params?.cameraId,
    limit: params?.limit,
    window_minutes: params?.windowMinutes,
    lookback_hours: params?.lookbackHours,
    stride_minutes: params?.strideMinutes,
    created_by: params?.createdBy,
  });
  return response.data;
}

export async function captureSilentWindowSample(input: {
  policyId: string;
  orgId?: string;
  siteId?: string;
  zoneId?: string;
  clipSeconds?: number;
}): Promise<SeedReviewSamplesResponse> {
  const response = await api.post<SeedReviewSamplesResponse>(
    '/api/v1/accuracy/review-samples/capture-silent-window',
    {
      policy_id: input.policyId,
      org_id: input.orgId,
      site_id: input.siteId,
      zone_id: input.zoneId,
      clip_seconds: input.clipSeconds ?? 20,
    },
  );
  return response.data;
}

export async function createReviewLabel(params: {
  reviewSampleId: string;
  labelOutcome:
    | 'true_positive'
    | 'false_positive'
    | 'duplicate'
    | 'insufficient_evidence'
    | 'true_negative'
    | 'missed_positive';
  reviewerIdentity?: string;
  notes?: string;
  labelSource?: string;
  labelPayload?: Record<string, any>;
}): Promise<ReviewLabelResponse> {
  const response = await api.post<ReviewLabelResponse>('/api/v1/accuracy/review-labels', {
    review_sample_id: params.reviewSampleId,
    label_outcome: params.labelOutcome,
    reviewer_identity: params.reviewerIdentity,
    notes: params.notes,
    label_source: params.labelSource,
    label_payload: params.labelPayload,
  });
  return response.data;
}

export async function createEvaluationRun(params?: {
  orgId?: string;
  siteId?: string;
  policyType?: string;
  pipelineName?: string;
  runtimeFamily?: string;
  deviceId?: string;
  cameraId?: string;
  /** Pin the evaluator corpus to one edge runtime build (required when multiple versions exist in-queue). */
  runtimeVersion?: string;
  zoneId?: string;
  /** ISO timestamps — only include review_samples created in this window. */
  sampleCreatedAfter?: string;
  sampleCreatedBefore?: string;
  labelingMethod?: string;
  createdBy?: string;
}): Promise<EvaluationRunResponse> {
  const response = await api.post<EvaluationRunResponse>('/api/v1/accuracy/evaluation-runs', {
    org_id: params?.orgId,
    site_id: params?.siteId,
    policy_type: params?.policyType,
    pipeline_name: params?.pipelineName,
    runtime_family: params?.runtimeFamily,
    device_id: params?.deviceId,
    camera_id: params?.cameraId,
    runtime_version: params?.runtimeVersion,
    zone_id: params?.zoneId,
    sample_created_after: params?.sampleCreatedAfter,
    sample_created_before: params?.sampleCreatedBefore,
    labeling_method: params?.labelingMethod,
    created_by: params?.createdBy,
  });
  return response.data;
}
